/**
 * components/email/GroupsPanel.tsx — distribution lists ("groups").
 *
 * A group is one source address that fans out to 2..50 destinations.
 * Backed by:
 *   GET    /groups
 *   POST   /groups                        {source, destinations[]}
 *   POST   /groups/:source/destinations   {destination}
 *   DELETE /groups/:source/destinations/:aliasId
 *   DELETE /groups/:source
 * Each destination is an alias row, so the plan's alias cap applies —
 * the create modal shows the impact.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, apiData, fetchGroups, friendlyError, isEmail, MailGroup, useEscape, v2Base } from './emailApi';

const MIN_MEMBERS = 2;
const MAX_MEMBERS = 50;

export const GROUP_COPY = 'A group address delivers to every member. Members can reply from the group address (send-as).';

const CreateGroupModal: React.FC<{
  domainRowId: string;
  domain: string;
  existingSources: string[];
  aliasCount: number;
  maxAliases: number | null;
  onClose: () => void;
  onCreated: () => void;
}> = ({ domainRowId, domain, existingSources, aliasCount, maxAliases, onClose, onCreated }) => {
  useEscape(onClose);
  const [local, setLocal] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const parsed = useMemo(() => {
    const raw = text.split(/[\n,;\s]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    const seen = new Set<string>();
    const valid: string[] = [];
    const invalid: string[] = [];
    for (const r of raw) {
      if (seen.has(r)) continue;
      seen.add(r);
      if (isEmail(r)) valid.push(r); else invalid.push(r);
    }
    return { valid, invalid };
  }, [text]);

  const localClean = local.trim().toLowerCase();
  const localOk = /^[a-z0-9][a-z0-9._+-]{0,63}$/.test(localClean);
  const source = `${localClean}@${domain}`;
  const sourceTaken = existingSources.includes(source);
  const selfMember = parsed.valid.includes(source);
  const count = parsed.valid.length;
  const countOk = count >= MIN_MEMBERS && count <= MAX_MEMBERS;
  const projected = aliasCount + count;
  const overCap = maxAliases !== null && projected > maxAliases;
  const canSubmit = localOk && !sourceTaken && countOk && parsed.invalid.length === 0 && !selfMember && !overCap && !busy;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      await apiData(`${v2Base(domainRowId)}/groups`, {
        method: 'POST',
        body: JSON.stringify({ source, destinations: parsed.valid }),
      });
      onCreated();
    } catch (e2: any) {
      setErr(friendlyError(e2, 'Couldn\'t create the group'));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-primary-navy/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-group-title"
        onClick={e => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-2xl border border-gray-200 shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h3 id="create-group-title" className="text-lg font-semibold text-primary-navy">Create group</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">{GROUP_COPY}</p>
          <div>
            <label htmlFor="group-local" className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Group address</label>
            <div className="flex">
              <input
                id="group-local"
                value={local}
                onChange={e => setLocal(e.target.value)}
                placeholder="team"
                autoFocus
                aria-invalid={!!localClean && (!localOk || sourceTaken)}
                aria-describedby="group-local-help"
                className="w-full px-3 py-2 border border-gray-300 rounded-l-lg text-primary-navy font-mono focus:outline-none focus:ring-2 focus:ring-secondary-500"
              />
              <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 rounded-r-lg bg-gray-50 text-sm text-gray-600 font-mono">@{domain}</span>
            </div>
            <p id="group-local-help" className="text-xs mt-1 text-gray-500">
              {localClean && !localOk && 'Use letters, digits, dots, dashes or underscores only.'}
              {localClean && localOk && sourceTaken && `${source} is already a group or alias.`}
              {(!localClean || (localOk && !sourceTaken)) && 'Letters, digits, dots, dashes, underscores.'}
            </p>
          </div>
          <div>
            <label htmlFor="group-members" className="block text-xs uppercase tracking-wider text-gray-500 mb-1">
              Members ({MIN_MEMBERS}–{MAX_MEMBERS} addresses, one per line)
            </label>
            <textarea
              id="group-members"
              value={text}
              onChange={e => setText(e.target.value)}
              rows={6}
              placeholder={`alice@${domain}\nbob@${domain}\nexternal@example.com`}
              aria-describedby="group-members-help"
              aria-invalid={parsed.invalid.length > 0 || (text.trim() !== '' && !countOk)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy font-mono text-sm focus:outline-none focus:ring-2 focus:ring-secondary-500"
            />
            <div id="group-members-help" className="text-xs mt-1 space-y-1">
              <div className="text-gray-500">
                <strong className="text-primary-navy">{count}</strong> valid address{count === 1 ? '' : 'es'}
                {count > 0 && count < MIN_MEMBERS && ` — add at least ${MIN_MEMBERS - count} more`}
                {count > MAX_MEMBERS && ` — too many, limit is ${MAX_MEMBERS}`}
                {selfMember && ' — a group can\'t be its own member'}
              </div>
              {parsed.invalid.length > 0 && (
                <div role="alert" className="text-primary-navy">
                  Not valid email addresses: <span className="font-mono">{parsed.invalid.slice(0, 5).join(', ')}{parsed.invalid.length > 5 ? '…' : ''}</span>
                </div>
              )}
            </div>
          </div>
          <div className={`text-xs rounded-lg p-3 border ${overCap ? 'bg-secondary-100 border-secondary-500 text-primary-navy' : 'bg-secondary-50 border-secondary-200 text-primary-navy/80'}`} role={overCap ? 'alert' : undefined}>
            <strong>Alias impact:</strong> each member is one alias. This group adds {count} → {projected} alias{projected === 1 ? '' : 'es'} in use
            {maxAliases !== null ? <> of your plan's {maxAliases}</> : null}.
            {overCap && ' That exceeds the plan limit — remove members or upgrade.'}
          </div>
          {err && <div role="alert" className="text-sm text-primary-navy bg-secondary-100 border border-secondary-500 rounded-lg p-2">🚨 {err}</div>}
        </div>
        <div className="p-5 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-primary-navy" disabled={busy}>Cancel</button>
          <button type="submit" disabled={!canSubmit} className="text-sm bg-secondary-500 text-primary-navy font-medium px-4 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50">
            {busy ? 'Creating…' : `Create ${localClean ? source : 'group'}`}
          </button>
        </div>
      </form>
    </div>
  );
};

const GroupRow: React.FC<{
  domainRowId: string;
  g: MailGroup;
  locked: boolean;
  onChanged: () => void;
}> = ({ domainRowId, g, locked, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [newDest, setNewDest] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const base = `${v2Base(domainRowId)}/groups/${encodeURIComponent(g.source_email)}`;
  const members = g.destinations || [];

  const addMember = async (e: React.FormEvent) => {
    e.preventDefault();
    const d = newDest.trim().toLowerCase();
    if (!isEmail(d)) { setErr('Enter a full email address.'); return; }
    if (members.some(m => m.destination.toLowerCase() === d)) { setErr('Already a member.'); return; }
    if (members.length >= MAX_MEMBERS) { setErr(`Groups hold at most ${MAX_MEMBERS} members.`); return; }
    setBusy(true);
    setErr(null);
    try {
      await apiData(`${base}/destinations`, { method: 'POST', body: JSON.stringify({ destination: d }) });
      setNewDest('');
      setAdding(false);
      onChanged();
    } catch (e2: any) {
      setErr(friendlyError(e2, 'Couldn\'t add the member'));
    } finally { setBusy(false); }
  };

  const removeMember = async (aliasId: number, destination: string) => {
    if (members.length <= MIN_MEMBERS) {
      if (!window.confirm(`Removing ${destination} leaves fewer than ${MIN_MEMBERS} members, so ${g.source_email} would stop being a group. Continue?`)) return;
    } else if (!window.confirm(`Remove ${destination} from ${g.source_email}?`)) return;
    setBusy(true);
    setErr(null);
    try {
      await api(`${base}/destinations/${aliasId}`, { method: 'DELETE' });
      onChanged();
    } catch (e2: any) {
      setErr(friendlyError(e2, 'Couldn\'t remove the member'));
    } finally { setBusy(false); }
  };

  const removeGroup = async () => {
    if (!window.confirm(`Delete the group ${g.source_email}? Mail sent to it will bounce.`)) return;
    setBusy(true);
    setErr(null);
    try {
      await api(base, { method: 'DELETE' });
      onChanged();
    } catch (e2: any) {
      setErr(friendlyError(e2, 'Couldn\'t delete the group'));
    } finally { setBusy(false); }
  };

  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-primary-navy break-all">
            {g.source_email}
            <span className="ml-2 text-xs font-sans text-gray-500">{members.length} member{members.length === 1 ? '' : 's'}</span>
          </div>
          <ul className="flex flex-wrap gap-1.5 mt-2" aria-label={`Members of ${g.source_email}`}>
            {members.map(m => (
              <li key={m.alias_id} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${m.active ? 'bg-secondary-50 border-secondary-200 text-primary-navy' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                <span className="font-mono break-all">{m.destination}</span>
                {!m.active && <span>(paused)</span>}
                {!locked && (
                  <button
                    type="button"
                    onClick={() => removeMember(m.alias_id, m.destination)}
                    disabled={busy}
                    aria-label={`Remove ${m.destination} from ${g.source_email}`}
                    className="ml-0.5 text-primary-navy hover:text-primary-700 font-semibold leading-none disabled:opacity-50"
                  >×</button>
                )}
              </li>
            ))}
          </ul>
          {adding && (
            <form onSubmit={addMember} className="mt-2 flex gap-2 flex-wrap">
              <label htmlFor={`add-${g.source_email}`} className="sr-only">New member address</label>
              <input
                id={`add-${g.source_email}`}
                value={newDest}
                onChange={e => setNewDest(e.target.value)}
                placeholder="member@example.com"
                autoFocus
                className="flex-1 min-w-[200px] px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-primary-navy font-mono focus:outline-none focus:ring-2 focus:ring-secondary-500"
              />
              <button type="submit" disabled={busy || !newDest} className="text-sm bg-secondary-500 text-primary-navy font-medium px-3 py-1.5 rounded-lg hover:bg-secondary-600 disabled:opacity-50">Add</button>
              <button type="button" onClick={() => { setAdding(false); setNewDest(''); setErr(null); }} className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg text-primary-navy hover:bg-gray-50">Cancel</button>
            </form>
          )}
        </div>
        {!locked && (
          <div className="flex items-center gap-2">
            {!adding && (
              <button type="button" onClick={() => setAdding(true)} disabled={busy || members.length >= MAX_MEMBERS} className="text-xs text-primary-navy hover:underline disabled:opacity-50" aria-label={`Add member to ${g.source_email}`}>
                + Add member
              </button>
            )}
            <button type="button" onClick={removeGroup} disabled={busy} className="text-xs text-primary-navy hover:underline font-semibold disabled:opacity-50" aria-label={`Delete group ${g.source_email}`}>
              Delete group
            </button>
          </div>
        )}
      </div>
      {err && <div role="alert" className="mt-2 text-xs text-primary-navy bg-secondary-100 border border-secondary-500 rounded-lg p-2">🚨 {err}</div>}
    </li>
  );
};

export const GroupsPanel: React.FC<{
  domainRowId: string;
  domain: string;
  /** Current alias rows (regular aliases + group members) for the impact note. */
  aliasCount: number;
  maxAliases: number | null;
  locked?: boolean;
  connectPath?: string;
  /** Parent re-fetches aliases when a group changes. */
  onChanged?: () => void;
}> = ({ domainRowId, domain, aliasCount, maxAliases, locked = false, connectPath, onChanged }) => {
  const [groups, setGroups] = useState<MailGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const rows = await fetchGroups(domainRowId);
      setGroups(Array.isArray(rows) ? rows.filter(r => r.is_group !== false) : []);
      setError(null);
    } catch (e: any) {
      setError(friendlyError(e, 'Couldn\'t load groups'));
      setGroups(prev => prev ?? []);
    }
  }, [domainRowId]);

  useEffect(() => { load(); }, [load]);

  const changed = () => { load(); onChanged?.(); };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-primary-navy">Groups &amp; distribution lists</h3>
          <p className="text-sm text-gray-600 mt-1">
            {GROUP_COPY}
            {connectPath && <> See <Link to={connectPath} className="underline">Connect your devices</Link> for adding a send-as identity.</>}
          </p>
        </div>
        {!locked && (
          <button type="button" onClick={() => setCreateOpen(true)} className="text-sm bg-secondary-500 text-primary-navy font-medium px-3 py-1.5 rounded-lg hover:bg-secondary-600">
            + Create group
          </button>
        )}
      </div>

      {error && (
        <div role="alert" className="text-sm text-primary-navy bg-secondary-100 border border-secondary-500 rounded-lg p-3">
          🚨 {error} <button type="button" onClick={load} className="underline ml-2">Retry</button>
        </div>
      )}

      {groups === null && !error && <div className="text-sm text-gray-500 py-4">Loading groups…</div>}
      {groups && groups.length === 0 && !error && (
        <div className="text-sm text-gray-500 py-6 text-center">
          No groups yet. Create one like <span className="font-mono">team@{domain}</span> that delivers to several people.
        </div>
      )}
      {groups && groups.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {groups.map(g => <GroupRow key={g.source_email} domainRowId={domainRowId} g={g} locked={locked} onChanged={changed} />)}
        </ul>
      )}

      {createOpen && (
        <CreateGroupModal
          domainRowId={domainRowId}
          domain={domain}
          existingSources={(groups || []).map(g => g.source_email.toLowerCase())}
          aliasCount={aliasCount}
          maxAliases={maxAliases}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); changed(); }}
        />
      )}
    </div>
  );
};

export default GroupsPanel;
